/**
 * AdBanner — Google AdSense responsive banner.
 *
 * Guarantees:
 *  • Lazy-loaded via IntersectionObserver (init only when visible).
 *  • Idempotent — `adsbygoogle.push` runs at most once per <ins> element.
 *  • Handles ad-blocker / unfilled / missing-slot states → collapses container.
 *  • Never leaves a large blank area or blocks navigation.
 *  • No CLS: container has zero reserved height by default; when the ad is
 *    served, AdSense's own `<ins>` grows the container naturally.
 *
 * Publisher script must be loaded once in `index.html`. This component only
 * renders the ad slot markup and pushes it to `window.adsbygoogle`.
 */
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const CLIENT = "ca-pub-4947611442778051";
// Timeout after which we assume the ad won't fill and collapse the container.
const FILL_TIMEOUT_MS = 3500;

export default function AdBanner({
  slot,
  format = "auto",
  layout,
  layoutKey,
  fullWidthResponsive = true,
  className,
  minHeight,
  testid = "ad-banner",
}) {
  const insRef = useRef(null);
  const pushedRef = useRef(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const el = insRef.current;
    if (!el) return;

    // If no slot is configured (env-driven), skip entirely — silently collapse.
    if (!slot) {
      setCollapsed(true);
      return;
    }

    let observer;
    let fillTimer;
    let cancelled = false;

    const push = () => {
      if (pushedRef.current || cancelled) return;
      pushedRef.current = true;
      try {
        // Guard: adsbygoogle may be blocked (adblocker) → collapse.
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({});
      } catch {
        setCollapsed(true);
        return;
      }
      // If the ad doesn't fill within a reasonable window, collapse.
      fillTimer = setTimeout(() => {
        if (cancelled) return;
        const status = el.getAttribute("data-ad-status");
        // "unfilled" or still no status → collapse. "filled" → keep.
        if (status !== "filled") setCollapsed(true);
      }, FILL_TIMEOUT_MS);
    };

    // Lazy: only initialize when the element scrolls into view.
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              push();
              observer.disconnect();
              break;
            }
          }
        },
        { rootMargin: "200px 0px" },
      );
      observer.observe(el);
    } else {
      // Fallback for very old browsers.
      push();
    }

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      if (fillTimer) clearTimeout(fillTimer);
    };
  }, [slot]);

  if (collapsed) return null;

  return (
    <div
      className={cn("ad-banner w-full overflow-hidden", className)}
      data-testid={testid}
      aria-hidden={false}
      style={minHeight ? { minHeight } : undefined}
    >
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block", textAlign: "center" }}
        data-ad-client={CLIENT}
        data-ad-slot={slot}
        data-ad-format={format}
        data-ad-layout={layout}
        data-ad-layout-key={layoutKey}
        data-full-width-responsive={fullWidthResponsive ? "true" : "false"}
      />
    </div>
  );
}

/**
 * Convenience: env-driven slot registry so publisher can rotate ad units
 * without a code change. When an env var is missing the ad silently collapses.
 */
export const AD_SLOTS = {
  dashboard: process.env.REACT_APP_ADSENSE_SLOT_DASHBOARD || "",
  insights:  process.env.REACT_APP_ADSENSE_SLOT_INSIGHTS  || "",
  recurring: process.env.REACT_APP_ADSENSE_SLOT_RECURRING || "",
  about:     process.env.REACT_APP_ADSENSE_SLOT_ABOUT     || "",
  landing:   process.env.REACT_APP_ADSENSE_SLOT_LANDING   || "",
  features:  process.env.REACT_APP_ADSENSE_SLOT_FEATURES  || "",
  blog:      process.env.REACT_APP_ADSENSE_SLOT_BLOG      || "",
};
